import { useCallback, useEffect, useState } from 'react';
import { api, type EstadoServicio, type VersionReportada } from '../api';
import { fechaYHora } from '../fechas';
import { Tabla } from './Tabla';

/**
 * Qué versión corre en cada parte de la plataforma, y qué versión reporta
 * cada canal del cliente.
 *
 * Son dos preguntas distintas que se confunden seguido. «¿Se desplegó?» se
 * contesta con la primera tabla: el commit que cada servicio dice que corre,
 * preguntado en vivo. «¿El portal ya subió el SDK?» se contesta con la
 * segunda: lo que llega en los propios datos. Las dos se respondían a mano
 * —con `curl` y deduciendo por los defectos— hasta que costó una semana.
 */
export function Versiones({ onError }: { onError: (err: unknown) => void }) {
  const [despliegue, setDespliegue] = useState<EstadoServicio[] | null>(null);
  const [reportadas, setReportadas] = useState<VersionReportada[] | null>(null);
  const [consultado, setConsultado] = useState<Date | null>(null);
  const [cargando, setCargando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [d, v] = await Promise.all([api.despliegue(), api.versionesReportadas()]);
      setDespliegue(d.servicios);
      setReportadas(v.versiones);
      setConsultado(new Date());
    } catch (err) {
      onError(err);
    } finally {
      setCargando(false);
    }
  }, [onError]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  if (!despliegue || !reportadas) return <p className="vacio">Cargando…</p>;

  const caidos = despliegue.filter((s) => !s.ok);

  return (
    <section className="versiones">
      <div className="versiones-cabecera">
        <h3>Lo que está corriendo</h3>
        <button type="button" className="secundario" onClick={() => void cargar()} disabled={cargando}>
          {cargando ? 'Consultando…' : 'Volver a consultar'}
        </button>
      </div>

      {caidos.length > 0 && (
        <p className="error">
          {caidos.length === 1
            ? `${caidos[0]?.servicio} no responde.`
            : `${caidos.map((s) => s.servicio).join(', ')} no responden.`}{' '}
          Lo demás sigue funcionando, pero lo que ese servicio hace no está ocurriendo.
        </p>
      )}

      <Tabla className="tabla-versiones">
        <thead>
          <tr>
            <th>Servicio</th>
            <th>Estado</th>
            <th>Commit</th>
            <th>Respuesta</th>
          </tr>
        </thead>
        <tbody>
          {despliegue.map((s) => (
            <tr key={s.servicio} className={s.ok ? '' : 'fila-caida'}>
              <td>{s.servicio}</td>
              <td>{s.ok ? 'responde' : (s.detalle ?? 'caído')}</td>
              <td>
                <code>{s.commit ?? '—'}</code>
              </td>
              <td>{s.latenciaMs === null ? '—' : s.servicio === 'panel' ? 'este mismo' : `${s.latenciaMs} ms`}</td>
            </tr>
          ))}
        </tbody>
      </Tabla>
      <p className="tenue">
        El commit es el que cada servicio dice que corre, preguntado{' '}
        {consultado ? `a las ${consultado.toLocaleTimeString('es-CL')}` : 'recién'}. Si dos servicios
        muestran commits distintos, el despliegue quedó a medias.
      </p>

      <h3 className="con-espacio">Lo que reporta cada canal</h3>
      {reportadas.length === 0 ? (
        <p className="vacio">Ningún canal ha enviado datos en los últimos siete días.</p>
      ) : (
        <Tabla className="tabla-versiones">
          <thead>
            <tr>
              <th>Canal</th>
              <th>Plataforma</th>
              <th>SDK</th>
              <th>Versión de la app</th>
              <th>Spans (7 días)</th>
              <th>Visto por última vez</th>
            </tr>
          </thead>
          <tbody>
            {reportadas.map((v) => (
              <tr key={`${v.canal}|${v.platform}|${v.sdk}|${v.app}`}>
                <td>{v.canal || 'desconocido'}</td>
                <td>{v.platform || '—'}</td>
                <td>
                  <code>{v.sdk || '—'}</code>
                </td>
                <td>
                  <code>{v.app || '—'}</code>
                </td>
                <td>{Number(v.spans).toLocaleString('es-CL')}</td>
                <td>{fechaYHora(v.visto)}</td>
              </tr>
            ))}
          </tbody>
        </Tabla>
      )}
      <p className="tenue">
        Sale de los propios datos: cada span trae la versión del SDK y de la aplicación que lo
        envió. Una versión vieja que sigue apareciendo es una app que sigue instalada, no un
        error del panel.
      </p>
    </section>
  );
}
